function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/* eslint-disable no-underscore-dangle */
import React from 'react';
import cn from 'clsx';
import { string, shape, arrayOf, bool } from 'prop-types';
import { useTranslation } from 'react-i18next';
import SocialFollow from '../../molecules/SocialFollow/index.jsx';
import Image from '../../atoms/Image/index.jsx';
import CorporateLogo from '../../atoms/CorporateLogo/index.jsx';
import List from '../../molecules/List/index.jsx';
import DeepLinksNavigation from '../../molecules/DeepLinksNavigation/index.jsx';
import Link from '../../atoms/Link/index.jsx';
import MenuLink from '../../atoms/MenuLink/index.jsx';
import BackgroundContainer from '../../atoms/BackgroundContainer/index.jsx';

/**
 * SiteFooter component.
 */
const SiteFooter = ({
  className,
  socialFollow,
  logo,
  company,
  partnerLogo,
  menuItems,
  copyrightText,
  sourcepoint,
  logoUrl,
  deepLinksTop,
  deepLinksBottom
}) => {
  const cmpEnabled = sourcepoint === null || sourcepoint === void 0 ? void 0 : sourcepoint.enabled;
  const {
    t
  } = useTranslation();
  const footerMenu = [...menuItems, cmpEnabled && {
    label: t('organisms.siteFooter.privacyItemLabel'),
    relativeUrl: '#',
    title: t('organisms.siteFooter.privacyItemTitle'),
    // set the cmpLink to be true
    cmpLink: 'true',
    onClick: evt => {
      var _window$_sp_;
      evt.preventDefault();
      if ((sourcepoint === null || sourcepoint === void 0 ? void 0 : sourcepoint.enabled) && ((_window$_sp_ = window._sp_) === null || _window$_sp_ === void 0 ? void 0 : _window$_sp_.gdpr)) {
        window._sp_.gdpr.loadPrivacyManagerModal(sourcepoint === null || sourcepoint === void 0 ? void 0 : sourcepoint.settingsUiId);
      }
    }
  }].filter(x => x);
  const companyText = (company === null || company === void 0 ? void 0 : company.name) ? ` ${company.name} ` : null;
  const getCopyrightYearText = (companyText || ' Immediate Media Company Ltd. ') + new Date().getFullYear();
  return /*#__PURE__*/React.createElement("footer", {
    className: cn('site-footer hidden-print', className),
    "data-feature": "FooterNavigation"
  }, /*#__PURE__*/React.createElement(BackgroundContainer, {
    className: "site-footer__brand-section",
    hasTopBorder: true
  }, /*#__PURE__*/React.createElement("div", {
    className: "site-footer__brand-section-inner site-footer__flex-row site-footer__flex-row--center container chromatic-ignore"
  }, logo && /*#__PURE__*/React.createElement(Image, _extends({
    fluid: true,
    className: "site-footer__brand-logo",
    maxWidth: 220,
    maxHeight: 40
  }, logo, {
    srcSetBreakpoints: [220, 440, 660, 880 // Up to 4x the max width
    ],

    sizes: ['220px'],
    link: {
      href: logoUrl,
      rel: 'home'
    }
  })), socialFollow && /*#__PURE__*/React.createElement("div", {
    className: "site-footer__social-follow"
  }, /*#__PURE__*/React.createElement(SocialFollow, socialFollow)))), /*#__PURE__*/React.createElement(BackgroundContainer, {
    className: "site-footer__corporate-section"
  }, /*#__PURE__*/React.createElement("section", {
    className: "site-footer__corporate-section-inner body-copy-small fluid-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "site-footer__flex-row container chromatic-ignore"
  }, menuItems.length > 1 && /*#__PURE__*/React.createElement(List, {
    className: "site-footer__menu"
  }, footerMenu.map((item, index) => /*#__PURE__*/React.createElement(List.Item, _extends({
    key: `${item.label}-${item.relativeUrl || item.absoluteUrl}`,
    className: "site-footer__menu-item",
    "data-component": "MenuLink",
    "data-index": index + 1,
    "data-item-name": item.label
  }, item.cmpLink ? {
    'data-cmp': item.cmpLink
  } : {}), /*#__PURE__*/React.createElement(MenuLink, _extends({
    inverted: true,
    className: "site-footer__menu-link"
  }, Object.assign(item, {
    children: null
  })), item.label))))), /*#__PURE__*/React.createElement("div", {
    className: "site-footer__flex-row container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "site-footer__corporate-logo chromatic-ignore"
  }, company && /*#__PURE__*/React.createElement(Link, {
    href: company.url,
    target: "_blank",
    rel: "noopener noreferrer"
  }, /*#__PURE__*/React.createElement(CorporateLogo, {
    name: company.name,
    logo: company.logo,
    width: 100,
    fillColor: "baseWhite"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "site-footer__copyright-section"
  }, copyrightText && /*#__PURE__*/React.createElement("small", {
    className: "site-footer__copyright-text body-copy-small"
  }, copyrightText), /*#__PURE__*/React.createElement("small", {
    className: "site-footer__copyright-text body-copy-small mt-xs"
  }, "\xA9", getCopyrightYearText)), partnerLogo && /*#__PURE__*/React.createElement(Image, _extends({
    fluid: true,
    className: "site-footer__partner-logo"
  }, partnerLogo, {
    maxWidth: 80,
    srcSetBreakpoints: [80, 160, 240, 320 // Up to 4x the max width
    ],

    sizes: ['80px']
  }))))), deepLinksTop.length > 0 && /*#__PURE__*/React.createElement(DeepLinksNavigation, {
    items: deepLinksTop,
    feature: "CorporateLinks",
    placement: "SiteFooter"
  }), deepLinksBottom.length > 0 && /*#__PURE__*/React.createElement(DeepLinksNavigation, {
    items: deepLinksBottom,
    feature: "SeoLinks"
  }));
};
SiteFooter.defaultProps = {
  socialFollow: null,
  className: null,
  logo: null,
  partnerLogo: null,
  company: {
    logo: null,
    url: 'https://www.immediate.co.uk',
    name: null
  },
  menuItems: [],
  copyrightText: null,
  logoUrl: null,
  sourcepoint: null,
  deepLinksTop: [],
  deepLinksBottom: []
};
SiteFooter.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** The social follow options. */
  socialFollow: shape(SocialFollow.propTypes),
  /** The brand logo. */
  logo: shape(Image.propTypes),
  /** The corporate brand logo. */
  company: shape({
    logo: shape(Image.propTypes),
    url: string,
    name: string
  }),
  /** The partner brand logo. */
  partnerLogo: shape(Image.propTypes),
  /** List of links for standard site hygiene footer */
  menuItems: arrayOf(shape(MenuLink.propTypes)),
  /** Copyright text on the footer. Excludes date */
  copyrightText: string,
  /** Sourcepoint settings */
  sourcepoint: shape({
    settingsUiId: string,
    enabled: bool
  }),
  /** The URL to link to from any brand logos. */
  logoUrl: string,
  deepLinksTop: arrayOf(shape(MenuLink.propTypes)),
  deepLinksBottom: arrayOf(shape(MenuLink.propTypes))
} : {};
export default SiteFooter;